import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ArrowLeft, LogOut, Menu, Wrench, X } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { consoleNav, type NavItem } from '@/app/roleNav';
import { SessionExpiryModal } from '@/auth/SessionExpiryModal';
import { cn } from '@/lib/cn';

/**
 * The internal SafeSphere operations console.
 *
 * Deliberately a different shell from AppShell rather than a section inside it. An
 * administrator is not a customer with extra menu items — they run the product, own
 * no vehicles, and land here rather than on a "your vehicles" dashboard. Sharing a
 * sidebar with the customer app was what made the two read as the same thing.
 *
 * The dark chrome is the whole point: you should never be unsure whether you are
 * looking at one customer's account or at every customer's data at once.
 */
function NavList({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.to === '/admin'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-[--radius-control] px-2.5 py-2 text-[13px] font-medium transition-colors',
                isActive
                  ? 'bg-white/10 text-white'
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              )
            }
          >
            <item.icon className="size-4 shrink-0" aria-hidden />
            {item.label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { user, signOut } = useAuth();

  return (
    <div className="flex h-full flex-col bg-[#0f172a]">
      <div className="flex h-14 items-center gap-2 border-b border-white/10 px-4">
        <Wrench className="size-5 text-white/70" aria-hidden />
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold tracking-tight text-white">
            AutoAware
          </p>
          <p className="truncate text-[11px] text-white/50">Operations console</p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-3" aria-label="Operations">
        <NavList items={consoleNav()} onNavigate={onNavigate} />
      </nav>

      <div className="border-t border-white/10 p-3">
        {/* Administrators can still open the customer app — they need to see what a
            customer sees. It is a deliberate switch, never the landing page. */}
        <Link
          to="/overview"
          onClick={onNavigate}
          className="mb-2 flex items-center gap-2.5 rounded-[--radius-control] px-2.5 py-2 text-[13px] font-medium text-white/60 transition-colors hover:bg-white/5 hover:text-white"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Customer app
        </Link>

        <div className="px-2.5 pb-2">
          <p className="truncate text-[13px] font-medium text-white">{user?.name}</p>
          <p className="truncate text-[12px] text-white/50">Administrator</p>
        </div>
        <button
          onClick={signOut}
          className="flex w-full items-center gap-2.5 rounded-[--radius-control] px-2.5 py-2 text-[13px] font-medium text-white/60 transition-colors hover:bg-white/5 hover:text-white"
        >
          <LogOut className="size-4" aria-hidden />
          Sign out
        </button>
      </div>
    </div>
  );
}

export function ConsoleShell() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-dvh bg-surface-sunken">
      <aside className="fixed inset-y-0 left-0 hidden w-60 lg:block">
        <SidebarContent />
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            className="absolute inset-0 bg-ink/40"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-64 shadow-lg">
            <button
              className="absolute right-2 top-3 rounded-[--radius-control] p-1.5 text-white/70 hover:bg-white/10"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
            >
              <X className="size-5" aria-hidden />
            </button>
            <SidebarContent onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}

      <div className="lg:pl-60">
        <header className="flex h-14 items-center gap-2 border-b border-line bg-surface px-4 lg:hidden">
          <button
            className="rounded-[--radius-control] p-1.5 text-ink-secondary hover:bg-surface-sunken"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
          >
            <Menu className="size-5" aria-hidden />
          </button>
          <span className="text-[13px] font-semibold text-ink">Operations console</span>
        </header>

        <main key={location.pathname} className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>

      <SessionExpiryModal />
    </div>
  );
}
